import React, { ReactNode, useEffect, useMemo, useRef } from "react";
import { ArcElement, Chart, Tooltip } from "chart.js";
import DataLabels from "chartjs-plugin-datalabels";
import { map } from "lodash";
import { Doughnut } from "react-chartjs-2";
import styled from "styled-components";
import AnimatedCount from "@charty/animations/count";
import { Item, Item as Serie } from "@charty/legend/item/model";
import { FontWeight, lighten } from "@glossy";
import { chart, content, useTheme } from "@glossy/theme";
import { Locale } from "@linkfluence/models/locale";
import {
  updateOnLegendHover,
  useBuildData,
  useDatasetTotal,
  useSortSeries,
} from "./data";
import { getOptions } from "./options";

Chart.register(ArcElement, DataLabels, Tooltip);

type ChartTooltip = {
  serie: Item;
  datasetTotal: number;
};
interface IProps {
  series: Serie[];
  hovered: string;
  locale: Locale;
  width: number;
  height: number;
  isWide: boolean;
  total?: number;
  onClickPie?: (uid: Serie["id"]) => void;
  displayTotal?: boolean;
  orderBy?: "natural" | "numeric";
  centerLabel?: string;
  chartTooltip?: (props: ChartTooltip) => ReactNode;
  disableAnimation?: boolean;
  isFullscreen?: boolean;
}

const Pie = styled.div<{ isWide: boolean }>`
  position: relative;
  height: ${(props) => !props.isWide && "calc(100% - 36px) !important"};
`;

const InnerText = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: ${(props) => content(props, "secondary")};
`;

const Total = styled.div`
  font-size: 28px;
`;

const Text = styled.div`
  font-size: 18px;
  font-weight: ${FontWeight.Bold};
`;

const DoughnutChart = ({
  series,
  locale,
  displayTotal = false,
  orderBy = "natural",
  centerLabel,
  onClickPie,
  chartTooltip,
  hovered,
  width,
  height,
  isWide,
  total,
  disableAnimation,
  isFullscreen,
}: IProps) => {
  const chartRef = useRef(null);
  const theme = useTheme();
  const sortedSeries = useSortSeries(series, orderBy);
  const ids = map(sortedSeries, "id");
  const primaryColor = chart({ theme });
  const colors = map(
    sortedSeries,
    (serie, cpt) => serie.color || lighten(0.04 * Number(cpt), primaryColor)
  );
  const data = useBuildData(sortedSeries, theme);
  const datasetTotal = useDatasetTotal(total, series);
  const options = useMemo(
    () =>
      getOptions(
        sortedSeries,
        locale,
        datasetTotal,
        centerLabel,
        onClickPie,
        chartTooltip,
        disableAnimation,
        isFullscreen
      ),
    [
      sortedSeries,
      locale,
      datasetTotal,
      centerLabel,
      onClickPie,
      chartTooltip,
      disableAnimation,
      isFullscreen,
    ]
  );

  useEffect(() => {
    if (chartRef.current) {
      updateOnLegendHover(chartRef.current, hovered, ids, colors, theme);
    }
  }, [chartRef, hovered, colors, ids, theme]);

  return (
    <>
      {width && height && (
        <Pie
          isWide={isWide}
          style={
            isWide ? { width: height, height } : { width, height: width - 36 }
          }
        >
          {displayTotal && (
            <InnerText>
              <Total>
                <AnimatedCount short locale={locale} value={datasetTotal} />
              </Total>
              {centerLabel && <Text>{centerLabel}</Text>}
            </InnerText>
          )}
          <Doughnut data={data} options={options} ref={chartRef} />
        </Pie>
      )}
    </>
  );
};

export default DoughnutChart;
